import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * A signature pad (REQUIREMENTS §2.6: "must work with a finger on a phone").
 *
 * Pointer events cover mouse, pen and touch with one code path.
 * `touch-action: none` stops the page from scrolling under the finger, which
 * is the usual reason a canvas signature "does not work" on a phone. The
 * backing store is scaled by devicePixelRatio so a stroke is crisp on a
 * phone screen and exports at that resolution.
 *
 * Direction-neutral on purpose: a signature is a drawing, and the canvas is
 * given `dir="ltr"` so its coordinate system does not flip with the page —
 * the pixels you draw are the pixels you get, in either language.
 */
export function SignatureCanvas({
  onChange,
  disabled = false,
}: {
  /** Called with a PNG data URL after each stroke, or null when cleared. */
  onChange: (dataUrl: string | null) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const last = useRef<{ x: number; y: number } | null>(null);
  const [hasInk, setHasInk] = useState(false);

  const setup = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ratio = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = Math.round(rect.width * ratio);
    canvas.height = Math.round(rect.height * ratio);
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#0f172a';
    // White, not transparent: the PDF and any dispute reproduce the PNG as
    // the soldier saw it, and "as seen" was ink on white.
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, rect.width, rect.height);
  }, []);

  useEffect(() => {
    setup();
    // Re-initialising on resize clears the pad. That is the honest choice:
    // rescaling a bitmap of a signature would distort it.
    const onResize = () => {
      setup();
      setHasInk(false);
      onChange(null);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [setup, onChange]);

  const point = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const start = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (disabled) return;
    // Capture keeps the stroke on the canvas when a finger drifts over its
    // edge. It is a nicety, not a requirement: if the browser refuses (it
    // throws for a pointer it does not know), drawing must still work.
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      /* draw without capture */
    }
    drawing.current = true;
    last.current = point(e);
  };

  const move = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current || !last.current) return;
    const ctx = e.currentTarget.getContext('2d');
    if (!ctx) return;
    const p = point(e);
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
    if (!hasInk) setHasInk(true);
  };

  const end = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current) return;
    drawing.current = false;
    last.current = null;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* was never captured */
    }
    onChange(e.currentTarget.toDataURL('image/png'));
  };

  const clear = () => {
    setup();
    setHasInk(false);
    onChange(null);
  };

  return (
    <div>
      <canvas
        ref={canvasRef}
        dir="ltr"
        onPointerDown={start}
        onPointerMove={move}
        onPointerUp={end}
        onPointerCancel={end}
        data-testid="signature-canvas"
        data-has-ink={hasInk ? 'true' : 'false'}
        aria-label={t('sign.canvasLabel')}
        className={`block h-40 w-full touch-none rounded-xl bg-white ring-2 ${
          disabled ? 'ring-slate-200' : hasInk ? 'ring-slate-900' : 'ring-slate-300'
        }`}
        style={{ touchAction: 'none' }}
      />
      <div className="mt-2 flex items-center justify-between">
        <p className="text-xs text-slate-500">{t('sign.canvasHint')}</p>
        <button
          type="button"
          onClick={clear}
          disabled={disabled || !hasInk}
          data-testid="signature-clear"
          className="text-xs text-slate-500 underline-offset-4 hover:underline disabled:opacity-40"
        >
          {t('sign.clear')}
        </button>
      </div>
    </div>
  );
}
